import { RefObject, useEffect } from 'react';

/** Pointer travel before a press stops being a click and becomes a pan. */
const DRAG_THRESHOLD_PX = 5;

/**
 * Controls that own the press themselves — a pan must never start on them,
 * however far the pointer then travels.
 */
const INTERACTIVE_SELECTOR =
  'a, button, input, select, textarea, label, [role="button"], [role="checkbox"], [role="menuitem"], [contenteditable="true"]';

/**
 * Pan a scroll container by dragging it with the pointer.
 *
 * The horizontal scroll of a wide table is otherwise reachable only by a
 * wheel/trackpad gesture: the native scrollbar sits at the bottom of a
 * viewport-tall scrollport, away from the rows being read, and macOS hides it
 * while idle. A mouse-only desktop user on a narrow window could not reach the
 * columns hidden behind the pinned actions cell at all.
 *
 * Deliberately narrow so nothing existing changes shape:
 * - mouse only — touch and pen already pan natively, and hijacking them would
 *   cost momentum scrolling;
 * - only while the content actually overflows horizontally, so a table that
 *   fits behaves exactly as before;
 * - a movement threshold, so an ordinary click still reaches sort headers,
 *   checkboxes, expand chevrons and the row-actions menu.
 */
export function useDragScroll(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let activePointerId: number | null = null;
    let startX = 0;
    let startY = 0;
    let startScrollLeft = 0;
    let startScrollTop = 0;
    /** Threshold crossed — the press is now a pan */
    let isPanning = false;
    /** A pan just ended; the click it produces is not the user's */
    let swallowNextClick = false;

    const onPointerDown = (event: PointerEvent) => {
      // A pan that ended outside the container may produce no click at all —
      // clear the flag here too so it can never poison a later real click.
      swallowNextClick = false;

      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      // Vertical-only overflow keeps its current wheel-driven behaviour
      if (el.scrollWidth <= el.clientWidth) return;
      if (event.target instanceof Element && event.target.closest(INTERACTIVE_SELECTOR)) return;

      activePointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      startScrollLeft = el.scrollLeft;
      startScrollTop = el.scrollTop;
      isPanning = false;
    };

    const onPointerMove = (event: PointerEvent) => {
      if (activePointerId !== event.pointerId) return;

      const dx = event.clientX - startX;
      const dy = event.clientY - startY;

      if (!isPanning) {
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
        isPanning = true;
        swallowNextClick = true;
        // Capture only now: taking it on pointerdown would retarget the
        // compatibility mouse events and break clicks on inner controls.
        el.setPointerCapture?.(event.pointerId);
        // `data-dragging` drives cursor + user-select (see TableScrollArea)
        el.toggleAttribute('data-dragging', true);
        // A selection started before the threshold would keep growing under the pan
        document.getSelection()?.removeAllRanges();
      }

      // Grab-the-surface model: the content follows the pointer on both axes
      el.scrollLeft = startScrollLeft - dx;
      el.scrollTop = startScrollTop - dy;
    };

    const onPointerEnd = (event: PointerEvent) => {
      if (activePointerId !== event.pointerId) return;

      if (isPanning) {
        if (el.hasPointerCapture?.(event.pointerId)) el.releasePointerCapture(event.pointerId);
        el.removeAttribute('data-dragging');
      }
      activePointerId = null;
      isPanning = false;
    };

    // Capture phase: React delegates onClick at the root, so stopping the event
    // here keeps it from ever reaching a sort header or a row action.
    const onClickCapture = (event: MouseEvent) => {
      if (!swallowNextClick) return;
      swallowNextClick = false;
      event.preventDefault();
      event.stopPropagation();
    };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerEnd);
    el.addEventListener('pointercancel', onPointerEnd);
    el.addEventListener('click', onClickCapture, true);

    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerEnd);
      el.removeEventListener('pointercancel', onPointerEnd);
      el.removeEventListener('click', onClickCapture, true);
    };
  }, [ref]);
}
